import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { requestOtp, verifyOtp, type PatientSession } from '../api/patientAuth'
import {
  SPECIALTIES,
  fetchSpecialists,
  fetchSpecialtyCounts,
  specialistsKey,
  specialtyCountsKey,
  submitSpecialistRequest,
  NAMED_BEYOND_THE_FIFTEEN
} from '../api/specialists'
import { Button } from '../components/Button'
import { CountChip } from '../components/CountChip'
import { EmptyState } from '../components/EmptyState'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { SearchInput } from '../components/SearchInput'
import { SpecialistRow } from '../components/SpecialistRow'
import { SearchReading } from '../components/search/SearchReading'
import {
  searchDestination,
  type SearchDestination,
} from '../components/search/destination'
import { ApiError } from '../lib/api'

/**
 * Specialists entry (M4-T07; grouped-by-specialty M12-T05) at `/specialists`.
 * The default view lists the closed 15 specialties with live counts; a name
 * search or a specialty chip switches to the matching specialists, and `?q=`
 * (home suggestions) searches by name. Tapping a specialist opens their profile
 * (`/specialists/:slug`) — read-only; appointment booking is a later domain.
 */
export function SpecialistSearch(): ReactNode {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // IN THE URL, NOT IN STATE (M33-T04). A result a patient shares or reloads has
  // to keep the specialty that produced it; held in component state it was lost
  // on the first refresh, and the profile had nothing to be told either.
  const specialty = searchParams.get('specialty')
  const [q, setQ] = useState(() => searchParams.get('q') ?? '')
  const [notListed, setNotListed] = useState(false)

  // WHAT THE SEARCH DECIDED, REPORTED UP (M33-T03, ADR-0062). The chip row and
  // the not-listed door are answers too, and they were offered as peers beside
  // the reading panel's own answer — four calls to action on one screen, which
  // is what the owner hit on `shortness of breath`. The panel owns the decision;
  // this is the page agreeing with it rather than guessing.
  const [destination, setDestination] = useState<SearchDestination | null>(null)

  // A name search or a chosen specialty switches from the grouped entry to the
  // matching specialists.
  const browsing = q.trim() !== '' || specialty !== null

  function writeParams(slug: string | null, query: string) {
    setSearchParams(
      () => {
        // Built from what the screen is SHOWING, not from the previous params.
        // `q` lives in component state and was never written back, so carrying
        // the old string forward produced a shared link describing a search
        // nobody had run.
        const next = new URLSearchParams()
        if (query !== '') next.set('q', query)
        if (slug !== null) next.set('specialty', slug)
        return next
      },
      // A filter is not a page: Back should leave the search, not undo a chip.
      { replace: true },
    )
  }

  function pickSpecialty(slug: string | null) {
    if (notListed) setNotListed(false)
    writeParams(slug, q)
  }

  /** The panel's destination-2 action (ADR-0062). It CLEARS the query, which a
   * chip deliberately does not: `specialty` and `q` are ANDed, and `q` is
   * matched against a doctor's name and sub-specialties — so keeping the symptom
   * that produced the answer would AND it to nothing and leave the patient on an
   * empty list, told to rephrase, one tap after a correct answer. */
  function seeGeneralist(slug: string) {
    if (notListed) setNotListed(false)
    setQ('')
    writeParams(slug, '')
  }

  return (
    <PageShell measure={594}>
      <header
        className={`flex items-center gap-1.5 border-b border-line-1 px-3 py-2 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="rounded p-1.5 text-ink-2 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={18} />
        </button>
        <span className="text-[13px] font-medium text-ink-1">
          Find a specialist
        </span>
      </header>

      {/* One band for the whole body: nothing nested has to restate the
          measure, and no padding moves, so mobile is unchanged by
          construction. The chip strip keeps its own `overflow-x-auto`, so
          it scrolls within the 594px column rather than the viewport. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        <div className="px-4 pt-3">
          <SearchInput
            value={q}
            onChange={setQ}
            label="Search specialists"
            placeholder="Cardiologist, ENT, oncologist…"
          />
        </div>

        {/* Hidden once the resolver has answered: a row reading All, Cardiology,
          Orthopaedics, OBGYN under a symptom search invites the patient to pick
          a department for themselves, which is the question they came here to
          have answered. It stays for browsing and for a search we read
          literally. */}
        <div
          role="group"
          aria-label="Specialty"
          hidden={destination !== null}
          className="flex gap-1.5 overflow-x-auto px-4 py-3"
        >
          <SpecialtyChip
            label="All"
            active={specialty === null && !notListed}
            onClick={() => pickSpecialty(null)}
          />
          {SPECIALTIES.map((s) => (
            <SpecialtyChip
              key={s.slug}
              label={s.name}
              active={specialty === s.slug && !notListed}
              onClick={() => pickSpecialty(s.slug)}
            />
          ))}
        </div>

        {notListed ? (
          <NotListedForm />
        ) : (
          <div className="flex flex-1 flex-col">
            {browsing ? (
              <>
                {/* M19-T03 — the chip and the box AND together, and the screen
                  never said so. The chip sits a few hundred pixels up and reads
                  as navigation, so an empty list looked like the query's fault.
                  This is ADR-0036's active-filter line, same vocabulary as
                  `HospitalResults`: a readout of what the list is scoped to,
                  with the single action that widens it. */}
                {specialty !== null && (
                  <ActiveSpecialty
                    specialty={specialty}
                    onClear={() => pickSpecialty(null)}
                  />
                )}
                <SpecialistList
                  specialty={specialty}
                  q={q}
                  onOpen={(slug) => navigate(`/specialists/${slug}`)}
                  onClearSpecialty={() => pickSpecialty(null)}
                  onEmergency={() => navigate('/emergency')}
                  onNotListed={() => setNotListed(true)}
                  onDestination={setDestination}
                  onPickSpecialty={seeGeneralist}
                />
              </>
            ) : (
              <SpecialtyGroups onPick={pickSpecialty} />
            )}

            {/* M19-T02 — the door sits OUTSIDE the browsing branch, so it renders
              over the results too. It used to live inside `SpecialtyGroups`,
              which only renders while `browsing` is false, so the first
              keystroke hid it — from precisely the patient whose search had
              just found nothing. `DiagnosticsSearch.tsx:180-193` already gates
              its two links this way and is the shape copied here: the
              browse-all link is conditional, the not-listed door never is. */}
            {/* ...but it is NOT offered beside the reading panel's own hand-off.
              "Request a specialist" and the AMTC number are the same
              destination said twice, and ADR-0062 allows one primary action per
              result. */}
            {destination !== 'concierge' && destination !== 'emergency' && (
              <NotListedDoor onNotListed={() => setNotListed(true)} />
            )}
          </div>
        )}
      </PageBand>
    </PageShell>
  )
}

/** The default grouped-by-specialty entry: the 15 specialties with live counts.
 * The "can't find your specialist?" door used to live here; it now renders a
 * level up so the results view gets it too (M19-T02). */
function SpecialtyGroups({
  onPick,
}: {
  onPick: (slug: string) => void
}): ReactNode {
  const query = useQuery({
    queryKey: specialtyCountsKey(),
    queryFn: ({ signal }) => fetchSpecialtyCounts(signal),
  })

  if (query.isPending) {
    return (
      <p className="px-4 py-6 text-[13px] text-ink-3" role="status">
        Loading specialties…
      </p>
    )
  }
  if (query.isError) {
    return (
      <div className="px-4 pb-4">
        <RetryBox onRetry={() => void query.refetch()} />
      </div>
    )
  }

  return (
    <div className="flex flex-col px-4 pb-2">
      <ul className="flex flex-col">
        {query.data.map((s) => (
          <li key={s.slug}>
            <button
              type="button"
              onClick={() => onPick(s.slug)}
              className="flex w-full items-center gap-3 border-b border-line-1 py-3 text-left hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-ink-1">
                  {s.name}
                </span>
                <span className="block text-[11px] tabular-nums text-ink-3">
                  {s.specialist_count}{' '}
                  {s.specialist_count === 1 ? 'specialist' : 'specialists'} ·{' '}
                  {s.hospital_count}{' '}
                  {s.hospital_count === 1 ? 'hospital' : 'hospitals'}
                </span>
              </span>
              <Icon name="chevr" size={14} className="text-ink-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The "can't find your specialist?" AMTC lead entry — the whole answer to
 * "we don't have what you need". Markup is unchanged from when it lived inside
 * `SpecialtyGroups`; only its position in the tree moved, so it now renders
 * once beneath whichever view is showing rather than only the landing one
 * (M19-T02). */
function NotListedDoor({
  onNotListed,
}: {
  onNotListed: () => void
}): ReactNode {
  return (
    <div className="px-4 pb-4">
      <div className="rounded-r3 border border-line-1 bg-bg-soft p-3">
        <p className="text-[13px] font-medium text-ink-1">
          Can&apos;t find the specialist you need?
        </p>
        <p className="mt-0.5 text-[12px] text-ink-3">
          Tell us who you&apos;re looking for and the AMTC team will help find
          them.
        </p>
        <button
          type="button"
          onClick={onNotListed}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-r3 border border-accent bg-accent-bg px-3 py-2 text-[13px] font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Request a specialist <Icon name="chevr" size={14} />
        </button>
      </div>
    </div>
  )
}

/** The name of a specialty slug, as `CLAUDE.md` spells it. */
function specialtyName(slug: string): string {
  return (
    SPECIALTIES.find((s) => s.slug === slug)?.name ??
    // Not in the browse grid, but still a destination the page must be able to
    // name: General Practice. Without this the readout printed the raw slug.
    NAMED_BEYOND_THE_FIFTEEN.find((s) => s.slug === slug)?.name ??
    slug
  )
}

/** What the specialist list is scoped to, and the one action that widens it
 * (M19-T03). Deliberately the same shape and vocabulary as `ActiveFilter` in
 * `HospitalResults` — ADR-0036 already chose this voice, and two screens
 * explaining a filter in two different voices is its own small defect.
 *
 * Like its sibling it renders nothing when only a text search is active: the
 * search box already shows the term, and this must not add furniture to the
 * common case. */
function ActiveSpecialty({
  specialty,
  onClear,
}: {
  specialty: string
  onClear: () => void
}): ReactNode {
  return (
    <div
      role="group"
      aria-label="Active filter"
      className="flex items-center gap-2 px-4 py-3"
    >
      <span className="font-mono text-[10px] uppercase tracking-wide text-ink-3">
        Showing
      </span>
      {/* A READOUT, not a control — `readOnly` renders a span. ADR-0036 retired
          the chip control and kept only its vocabulary. */}
      <CountChip readOnly selected label={specialtyName(specialty)} />
      <button
        type="button"
        onClick={onClear}
        className="ml-auto rounded-r2 px-2 py-1 text-[12px] font-medium text-accent-ink hover:bg-accent-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Clear
      </button>
    </div>
  )
}

/** The specialists matching a specialty and/or name query. */
function SpecialistList({
  specialty,
  q,
  onOpen,
  onClearSpecialty,
  onEmergency,
  onNotListed,
  onDestination,
  onPickSpecialty,
}: {
  specialty: string | null
  q: string
  onOpen: (specialistSlug: string) => void
  onClearSpecialty: () => void
  onEmergency: () => void
  onNotListed: () => void
  /** Where this search ended, so the page can stop offering answers of its own
   * beside the panel's (M33-T03). */
  onDestination: (destination: SearchDestination | null) => void
  /** Scope the list to one specialty — the panel's destination-2 action. */
  onPickSpecialty: (specialtySlug: string) => void
}): ReactNode {
  const query = useQuery({
    queryKey: specialistsKey(specialty, q),
    queryFn: ({ signal }) => fetchSpecialists(specialty, q, signal),
  })
  const specialists = query.data?.data ?? []
  const interpretation = query.data?.interpretation ?? null

  // An emergency is not a search result. The server already refuses to return a
  // ranked list (ADR-0035); until M22-T12 this page ignored that and rendered
  // "No specialists found. Try another specialty or name." at someone
  // describing a heart attack. Suppress the count and the list entirely — the
  // alert is the whole answer.
  const isEmergency = interpretation?.route === 'emergency'

  // Understood, but nobody on the platform staffs it. The reading block says so
  // and offers the AMTC route, so the empty state must not also tell the patient
  // to "try another specialty or name" — that is advice to rephrase a query we
  // understood on the first try, and it contradicts the line directly above it.
  // Reported after render, never during one: the page stores it in state, and
  // setting state while rendering a child is how a loop starts.
  const destination =
    interpretation === null ? null : searchDestination(interpretation)
  useEffect(() => {
    onDestination(destination)
    // WITHOUT THIS CLEANUP THE CHIPS STAY HIDDEN. Backspacing to an empty box
    // unmounts this list, and the parent kept the last destination — so the
    // browse screen rendered with no specialty chips and no not-listed door
    // until a reload.
    return () => onDestination(null)
  }, [destination, onDestination])

  // ...and neither must a query we could not read at all: the panel directly
  // above has just handed it to AMTC, and "try another specialty or name" tells
  // the patient to rephrase it (M33-T03). The pointer to a door "below" was
  // wrong too — that door is now gated off on exactly this screen.
  const understoodButUnstaffed =
    interpretation !== null &&
    (interpretation.route === 'no_match' ||
      (interpretation.understood_as.length > 0 &&
        interpretation.understood_as.every((sp) => !sp.offered)))

  return (
    <>
      {interpretation !== null && (
        <div className="px-4">
          <SearchReading
            interpretation={interpretation}
            subject="specialists"
            onEmergency={onEmergency}
            onConcierge={onNotListed}
            onGeneralist={onPickSpecialty}
          />
        </div>
      )}

      {!isEmergency && (
        <p className="px-4 pb-1.5 text-xs text-ink-3" aria-live="polite">
          {query.isPending
            ? 'Loading…'
            : query.isError
              ? "Couldn't load specialists"
              : `${specialists.length} ${specialists.length === 1 ? 'specialist' : 'specialists'}`}
        </p>
      )}

      {isEmergency ? null : (
      <div className="flex flex-col gap-2 px-4 pb-2">
        {query.isPending ? (
          <Skeleton />
        ) : query.isError ? (
          <RetryBox onRetry={() => void query.refetch()} />
        ) : specialists.length === 0 ? (
          understoodButUnstaffed ? (
            /* The reading block above has already named the discipline and
               offered the AMTC route. Repeating "try another specialty or name"
               here would argue with it, so the empty state only confirms the
               list is empty and gets out of the way. */
            <EmptyState
              title="No specialists to list yet."
              body="The AMTC team will arrange this for you — see above."
            />
          ) :
          /* M17-T05 proof site #2. M19-T02 widened the body, which used to
             advise only "Try another specialty or name" — advice to repeat the
             thing that just failed, with the exit hidden.

             M19-T03 splits it again. When BOTH a specialty and a query are
             narrowing the list, the empty state names both as the cause and
             offers the repair, because that is the case the patient cannot
             diagnose: they typed a name they know is real and got nothing, and
             the other filter is off-screen. With one filter there is nothing to
             clear, and an empty state with a dead button is worse than one
             without — so the action appears only when it would do something. */
          specialty !== null && q.trim() !== '' ? (
            <EmptyState
              title="No specialists found."
              body={`No ${specialtyName(specialty)} specialist matches “${q.trim()}”. Both the specialty and your search are narrowing this list.`}
              action={
                <Button kind="secondary" onClick={onClearSpecialty}>
                  Clear {specialtyName(specialty)}
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No specialists found."
              body="Try another specialty or name, or tell us who you need below."
            />
          )
        ) : (
          specialists.map((s) => (
            <SpecialistRow key={s.id} specialist={s} onOpen={onOpen} />
          ))
        )}
      </div>
      )}
    </>
  )
}

function SpecialtyChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}): ReactNode {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        active
          ? 'h-8 shrink-0 whitespace-nowrap rounded-full border border-accent bg-accent px-3 text-[13px] font-medium text-accent-on focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
          : 'h-8 shrink-0 whitespace-nowrap rounded-full border border-line-2 bg-bg-app px-3 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
      }
    >
      {label}
    </button>
  )
}

/**
 * "My specialist isn't listed" — free text + optional detail + area, gated by a
 * phone OTP (the M10-T02 pattern), POSTed as an AMTC concierge lead (M14-T09).
 * The closed 15 specialties stand; this captures the long tail. On success it
 * shows the reference; the concierge follows up (no booking here).
 */
function NotListedForm(): ReactNode {
  const [description, setDescription] = useState('')
  const [details, setDetails] = useState('')
  const [area, setArea] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [session, setSession] = useState<PatientSession | null>(null)
  const [reference, setReference] = useState<string | null>(null)

  const requestMutation = useMutation({
    mutationFn: () => requestOtp(phone.trim()),
    onSuccess: () => setCodeSent(true),
  })
  const verifyMutation = useMutation({
    mutationFn: () => verifyOtp(phone.trim(), code.trim()),
    onSuccess: (s) => setSession(s),
  })
  const submitMutation = useMutation({
    mutationFn: () => {
      if (session === null) throw new Error('not verified')
      const trimmedDetails = details.trim()
      const trimmedArea = area.trim()
      return submitSpecialistRequest(session.token, {
        description: description.trim(),
        ...(trimmedDetails ? { details: trimmedDetails } : {}),
        ...(trimmedArea ? { area: trimmedArea } : {}),
      })
    },
    onSuccess: (res) => setReference(res.reference),
  })

  if (reference !== null) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-bed-good-bg text-bed-good">
          <Icon name="check" size={22} />
        </span>
        <h1 className="font-display text-d-xs font-medium tracking-display text-ink-1">
          Request sent
        </h1>
        <p className="text-[13px] text-ink-3">
          The AMTC team will be in touch. Your reference is{' '}
          <span className="font-semibold text-ink-1">{reference}</span>.
        </p>
      </div>
    )
  }

  const ready = description.trim().length >= 3 && session !== null

  return (
    <div className="flex flex-1 flex-col gap-5 px-4 py-5">
      <div>
        <h1 className="text-base font-bold text-ink-1">Request a specialist</h1>
        <p className="mt-0.5 text-[13px] text-ink-3">
          Can&apos;t find the specialist you need? Tell us who you&apos;re
          looking for and the AMTC team will help find them.
        </p>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium text-ink-1">
          What kind of specialist?
        </span>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          aria-label="What kind of specialist?"
          placeholder="e.g. Hepatologist, Paediatric neurologist…"
          className="rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium text-ink-1">
          Any details?{' '}
          <span className="font-normal text-ink-3">(optional)</span>
        </span>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={3}
          aria-label="Any details?"
          placeholder="Any context to help us find the right specialist…"
          className="rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium text-ink-1">
          Your area <span className="font-normal text-ink-3">(optional)</span>
        </span>
        <input
          type="text"
          value={area}
          onChange={(e) => setArea(e.target.value)}
          aria-label="Your area"
          placeholder="e.g. East Legon, Accra"
          className="rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </label>

      <div className="flex flex-col gap-2">
        <span className="text-[13px] font-medium text-ink-1">
          Verify your phone
        </span>
        {session ? (
          <p className="inline-flex w-fit items-center gap-1 rounded-full bg-bed-good-bg px-2.5 py-1 text-[13px] font-medium text-bed-good">
            <Icon name="check" size={14} /> Verified · {phone.trim()}
          </p>
        ) : (
          <>
            <div className="flex gap-2">
              <input
                type="tel"
                aria-label="Phone number"
                placeholder="Phone, e.g. +233…"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1 rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              />
              <button
                type="button"
                disabled={phone.trim().length < 8 || requestMutation.isPending}
                onClick={() => requestMutation.mutate()}
                className="shrink-0 rounded-r3 border border-accent bg-accent-bg px-3 text-[13px] font-medium text-accent-ink disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                {requestMutation.isPending
                  ? 'Sending…'
                  : codeSent
                    ? 'Resend'
                    : 'Send code'}
              </button>
            </div>
            {requestMutation.isError && (
              <p role="alert" className="text-[12px] text-bed-bad">
                {errMsg(requestMutation.error)}
              </p>
            )}
            {codeSent && (
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Verification code"
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="flex-1 rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                />
                <button
                  type="button"
                  disabled={code.trim().length < 4 || verifyMutation.isPending}
                  onClick={() => verifyMutation.mutate()}
                  className="shrink-0 rounded-r3 bg-accent px-4 text-[13px] font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  Verify
                </button>
              </div>
            )}
            {verifyMutation.isError && (
              <p role="alert" className="text-[12px] text-bed-bad">
                {errMsg(verifyMutation.error)}
              </p>
            )}
          </>
        )}
      </div>

      <button
        type="button"
        disabled={!ready || submitMutation.isPending}
        onClick={() => submitMutation.mutate()}
        className="mt-1 rounded-r3 bg-accent px-4 py-2.5 text-sm font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {submitMutation.isPending ? 'Sending…' : 'Send request'}
      </button>
      {submitMutation.isError && (
        <p role="alert" className="text-[12px] text-bed-bad">
          {errMsg(submitMutation.error)}
        </p>
      )}
    </div>
  )
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'rate_limited')
      return 'Too many requests. Please try again later.'
    return err.message
  }
  return 'Something went wrong. Please try again.'
}

function RetryBox({ onRetry }: { onRetry: () => void }): ReactNode {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-1 px-5 py-12 text-center"
    >
      <p className="text-[13px] text-ink-3">Couldn&apos;t load. Try again.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-1 rounded-full bg-accent px-4 py-1.5 text-[13px] font-medium text-accent-on hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Retry
      </button>
    </div>
  )
}

function Skeleton(): ReactNode {
  return (
    <div aria-hidden="true" className="flex flex-col gap-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="grid grid-cols-[44px_1fr] gap-3 rounded-r3 border border-line-1 p-3"
        >
          <div className="h-11 w-11 animate-pulse rounded-full bg-bg-sunken" />
          <div className="flex flex-col gap-2">
            <div className="h-3 w-1/2 animate-pulse rounded bg-bg-sunken" />
            <div className="h-2.5 w-1/3 animate-pulse rounded bg-bg-sunken" />
          </div>
        </div>
      ))}
    </div>
  )
}
